'use client';
import React, { FC, useEffect, useState } from 'react';
import { LuvImage } from '@/components/ui/LuvImage';
import { RichText } from '@/components';
import { Document } from '@contentful/rich-text-types';
import {
  getMediaComponent,
  isVideo,
} from '@/components/common/LandingPageHero/utils';
import { ExtendedTypeHomepageHeroFields } from '@/components/common/HomepageHero/HomepageHero';
import classNames from 'classnames';
import styles from '../HomepageHero.module.scss';
import { UniversalCta } from '../../UniversalCta';
import useAuth from '@/context/AuthContext';

export const FullImage: FC<ExtendedTypeHomepageHeroFields> = ({
  entryName,
  mobileMediaIsVideo,
  altText,
  typeImage,
  desktopMedia,
  mobileMedia,
  whiteTextDesktop,
  whiteTextMobile,
  subtext = '',
  headerText = '',
  cta,
  secondaryCTA,
  desktopCtaAlignment,
  loggedOutText,
  loggedInText,
}) => {
  const [hydrated, setHydrated] = useState(false);
  const { user } = useAuth();
  const useLoggedInText = !!user && loggedInText;
  const textToDisplay = useLoggedInText ? loggedInText : loggedOutText;
  const isDesktopVideo = isVideo(desktopMedia);

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <section
      data-testid={`homepage-hero-${entryName}`}
      className="mx-auto text-center"
    >
      <div className="relative lg:h-[650px]">
        {getMediaComponent(
          desktopMedia,
          'absolute h-auto w-full bg-cover bg-center lg:h-[650px] desktop-homepage-hero-bg hidden lg:block',
          isDesktopVideo,
        )}

        <div className="lg:relative lg:top-[112px]">
          <div className="block lg:hidden">
            {mobileMediaIsVideo ? (
              getMediaComponent(mobileMedia, 'min-h-[280px]', true)
            ) : (
              <LuvImage
                imageData={typeImage}
                alt={altText}
                height={648}
                width={750}
                loading="eager"
              />
            )}
          </div>
          <div
            className={classNames(
              'mx-auto p-6 lg:max-w-[605px] lg:bg-transparent',
              whiteTextDesktop ? 'lg:text-white' : '',
              whiteTextMobile ? 'text-white' : '',
            )}
          >
            {subtext && (
              <h4 className="text-d-base font-bold lg:mb-6 lg:text-d-h3">
                {subtext}
              </h4>
            )}
            <h1 className="mb-2 text-d-h2 font-bold lg:mb-8 lg:text-d-hero">
              {headerText}
            </h1>
            <div
              className={classNames(
                'flex flex-col lg:inline-flex',
                desktopCtaAlignment === 'row' ? 'gap-4 lg:flex-row' : 'gap-3',
                secondaryCTA && desktopCtaAlignment === 'row'
                  ? 'lg:w-[100%]'
                  : '',
              )}
            >
              {cta && (
                <UniversalCta
                  link={cta}
                  defaultButtonStyle="Primary"
                  className={secondaryCTA ? 'lg:grow lg:basis-0' : ''}
                />
              )}
              {secondaryCTA && (
                <UniversalCta
                  link={secondaryCTA}
                  defaultButtonStyle="Secondary"
                  className="lg:grow lg:basis-0"
                />
              )}
            </div>

            {textToDisplay && hydrated && (
              <div
                className={classNames(
                  `mt-5 lg:m-6 ${styles.homepageHero}`,
                  whiteTextDesktop ? 'lg:text-white' : '',
                  whiteTextMobile ? 'text-white' : '',
                )}
              >
                <RichText
                  content={textToDisplay as Document}
                  addParagraphMargins={false}
                  openLinkSameWindow={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
